import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Layout.module.css';
import tabStyles from './DashboardTabs.module.css';
import { Caption, ChartRenderer, ChartTypePreview, Input, Notify } from 'components/core';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  VisualFilteredSearchList,
  currentDashboardState,
} from 'states';
import { useDashboardService, useVisualizationService } from 'services';
import { CHART_TYPE, MOCK_CHART_DATA, MOCK_TABVIEW_DATA, normalizeChartType, RENDERABLE_CHART_TYPES } from '../../../../../constants';
import { getVizTypeConfig } from '../../../../../constants/vizTypeConfig';
import { allDashboards } from 'states/atoms';
import { ConfirmDialog } from 'molecules/confirmDialog/ConfirmDialog';

interface LayoutCol {
  id: number;
  data: Record<string, unknown>;
}
interface LayoutRow {
  rowId: number;
  cols: LayoutCol[];
}

interface LayoutProps {
  register: any;
  setValue: any;
  status: boolean;
  onVisualizationDeleted?: () => void;
}

interface Visualization {
  id: number;
  title: string;
  isChecked: boolean;
  chartType: string;
  chartData: any;
  visualizationCode: string;
  dashboards?: any;
}

/** A removal waiting for the admin to confirm it. */
type PendingRemoval = { kind: 'tile'; rowId: number; colId: number; index: number; data: number } | { kind: 'row'; rowId: number };

export const Layout = ({ register, setValue, status: _status, onVisualizationDeleted }: LayoutProps) => {
  const { t } = useTranslation();
  const [layoutConfig, setLayoutConfig]           = useState<any[]>([]);
  const [pendingRemoval, setPendingRemoval]       = useState<PendingRemoval | null>(null);
  const [visualizationList, setVisualizationList] = useState<Visualization[]>([]);
  const [renderElements, setRenderElements]       = useState(false);
  const [showModal, setShowModal]                 = useState(false);
  const [modalRowId, setModalRowId]               = useState(0);
  const [modalColIndex, setModalColIndex]         = useState(0);
  const [searchQuery, setSearchQuery]             = useState('');
  const [activeTypeFilter, setActiveTypeFilter]   = useState('all');
  const [selectedVizCode, setSelectedVizCode]     = useState('');
  const [reorderBanner, setReorderBanner]         = useState(false);
  const [dragRowIndex, setDragRowIndex]           = useState<number | null>(null);
  const [dropRowIndex, setDropRowIndex]           = useState<number | null>(null);

  const currentDashboard     = useRecoilValue(currentDashboardState);
  const visualizationService = useVisualizationService();
  const [visualisationFilteredList, setVisualisationFilteredList] = useRecoilState(VisualFilteredSearchList);
  const dashboardService     = useDashboardService();
  const [, setDashboards]    = useRecoilState(allDashboards);

  useEffect(() => {
    getDashboards();
    const raw = currentDashboard.data[0].layout.layoutConfig;
    if (raw) {
      try { setLayoutConfig(JSON.parse(raw.replace(/\n/g, ''))); }
      catch { setLayoutConfig([]); }
    } else {
      setLayoutConfig([]);
    }
  }, []);

  useEffect(() => {
    getVisualizationList();
    const tabEl = document.querySelector('#nav-layout-tab');
    if (tabEl) {
      tabEl.addEventListener('shown.bs.tab', () => setRenderElements(true));
      tabEl.addEventListener('hidden.bs.tab', () => setRenderElements(false));
    }
  }, []);

  useEffect(() => {
    if (Object.keys(currentDashboard).length > 0) {
      const config = [{ rowId: 0, cols: [{ id: 0, data: {} }] }];
      const raw = currentDashboard.data[0]['layout']['layoutConfig'];
      if (!raw) {
        setLayoutConfig(config);
        updateLayoutConfig(config);
      } else {
        try { setLayoutConfig(JSON.parse(raw.replace(/\n/g, ''))); }
        catch { setLayoutConfig(config); }
      }
    }
  }, [currentDashboard]);

  const getDashboards = () => {
    dashboardService.getDashboards().then(
      (response: any) => { if (response.length > 0) setDashboards(response); },
      (error) => Notify.error(error)
    );
  };

  const getVisualizationList = () => {
    visualizationService.getAllVisualizations().then(
      (response) => { if (response && response.length > 0) resetVisualizationList(response); },
      (error) => Notify.error(error)
    );
  };

  const resetVisualizationList = (visualizations: any[]) => {
    const tempArray: Visualization[] = visualizations.map((viz: any) => {
      let chartData = {};
      try {
        chartData = viz.data ? JSON.parse(viz.data[0].data.query) : {};
      } catch {
        chartData = {};
      }
      return {
        id: viz.id,
        title: viz.title,
        isChecked: false,
        chartType: viz.data?.[0]?.info?.chartType || '',
        chartData,
        visualizationCode: viz.visualizationCode,
        dashboards: viz.data?.[0]?.dashboards,
      };
    });
    setVisualizationList(tempArray);
    setVisualisationFilteredList(tempArray);
  };

  const updateLayoutConfig = (lc: any[]) => {
    setValue('layoutConfig', JSON.stringify(lc));
  };

  // ── HTML5 drag handlers ───────────────────────────────────────────────────
  const handleRowDragStart = (e: React.DragEvent, index: number) => {
    setDragRowIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleRowDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropRowIndex(index);
  };

  const handleRowDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = dragRowIndex;
    setDragRowIndex(null);
    setDropRowIndex(null);
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    const updated = [...layoutConfig];
    const [moved] = updated.splice(sourceIndex, 1);
    updated.splice(targetIndex, 0, moved);
    setLayoutConfig(updated);
    updateLayoutConfig(updated);
    setReorderBanner(true);
  };

  const handleRowDragEnd = () => {
    setDragRowIndex(null);
    setDropRowIndex(null);
  };

  const getBaseVizList = (): Visualization[] => {
    const dashId = currentDashboard.dashboardId || '';
    if (!dashId || dashId === 'newDashboard') return visualisationFilteredList as Visualization[];
    return (visualisationFilteredList as any[]).filter((viz: any) => {
      try {
        let parsed = viz.dashboards;
        if (!parsed) return false;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        return parsed.includes(dashId);
      } catch { return false; }
    });
  };

  const getDisplayedVizs = (): Visualization[] =>
    getBaseVizList().filter((viz: any) => {
      const matchType = activeTypeFilter === 'all' || (viz.chartType || '').toLowerCase() === activeTypeFilter;
      const matchSearch = (viz.title || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchType && matchSearch;
    });

  const chartTypes = Array.from(new Set(getBaseVizList().map((v: any) => (v.chartType || '').toLowerCase()).filter(Boolean)));
  const selectedViz = (visualisationFilteredList as any[]).find((v: any) => v.isChecked);
  const displayedVizs = getDisplayedVizs();

  const handleVizSelect = (viz: any) => {
    const code = viz.visualizationCode;
    const isAlreadySelected = selectedVizCode === code;
    setSelectedVizCode(isAlreadySelected ? '' : code);
    setVisualisationFilteredList((visualisationFilteredList as any[]).map((item: any) => ({
      ...item,
      isChecked: !isAlreadySelected && item.visualizationCode === code,
    })));
  };

  const onCancel = () => {
    setShowModal(false);
    setSelectedVizCode('');
    setSearchQuery('');
    setActiveTypeFilter('all');
    const reset = visualizationList.map((item) => ({ ...item, isChecked: false }));
    setVisualizationList(reset);
    setVisualisationFilteredList(reset);
  };

  const launchModal = (e: any, rowId: number, colIndex: number) => {
    e.preventDefault();
    getVisualizationList();
    setModalRowId(rowId);
    setModalColIndex(colIndex);
    setSelectedVizCode('');
    setSearchQuery('');
    setActiveTypeFilter('all');
    setShowModal(true);
  };

  const saveVisualMapping = (e: any, rowId: number, colIndex: number) => {
    e.preventDefault();
    const tempLayoutConfig = [...layoutConfig];
    let selectedChart: any, visualizationCode = '', selectedData: any = '';
    (visualisationFilteredList as any[]).forEach((viz: any) => {
      if (viz.isChecked) { selectedChart = viz.chartType; selectedData = viz.chartData; visualizationCode = viz.visualizationCode; }
    });
    tempLayoutConfig.forEach((m: any) => {
      if (m.rowId === rowId) {
        m.cols[colIndex].data = { chartType: selectedChart, chartData: selectedData };
        m.cols[colIndex].visualizationCode = visualizationCode;
      }
    });
    setLayoutConfig(tempLayoutConfig);
    updateLayoutConfig(tempLayoutConfig);
    onCancel();
  };

  const addRow = () => {
    const maxRowId = layoutConfig.length > 0 ? Math.max(...layoutConfig.map((r: any) => r.rowId)) : -1;
    const config = [...layoutConfig, { rowId: maxRowId + 1, cols: [{ id: 0, data: {} }] }];
    setLayoutConfig(config);
    updateLayoutConfig(config);
  };

  const removeRow = (rowId: number) => {
    // removeRow is only ever invoked for a row that currently exists, so
    // layoutConfig.length is always >= 1 here — guard against dropping the
    // last row instead, which would leave the dashboard with zero rows.
    if (layoutConfig.length <= 1) { Notify.error(t('LAYOUT.CANNOT_DELETE_DEFAULT')); return; }
    const config = layoutConfig.filter((r: any) => r.rowId !== rowId);
    setLayoutConfig(config);
    updateLayoutConfig(config);
  };

  const addColumn = (rowId: number) => {
    const config = layoutConfig.map((row: any) => {
      if (row.rowId !== rowId) return row;
      if (row.cols.length >= 3) { Notify.error(t('LAYOUT.MAX_COLS_REACHED')); return row; }
      return { ...row, cols: [...row.cols, { id: row.cols[row.cols.length - 1].id + 1, data: {} }] };
    });
    setLayoutConfig(config);
    updateLayoutConfig(config);
  };

  // Taking away a chart, or a row with charts in it, asks first; an empty slot is removed straight away.
  const askBeforeRemovingColumn = (rowId: number, colId: number, index: number, data: number) => {
    if (data > 0) setPendingRemoval({ kind: 'tile', rowId, colId, index, data });
    else deleteColumn(rowId, colId, index, data);
  };

  const askBeforeRemovingRow = (rowId: number) => {
    const row = layoutConfig.find((r: LayoutRow) => r.rowId === rowId);
    const hasCharts = !!row?.cols.some((col: LayoutCol) => Object.keys(col.data).length > 0);
    if (hasCharts && layoutConfig.length > 1) setPendingRemoval({ kind: 'row', rowId });
    else removeRow(rowId);
  };

  const confirmRemoval = () => {
    const pending = pendingRemoval;
    setPendingRemoval(null);
    if (!pending) return;
    if (pending.kind === 'tile') deleteColumn(pending.rowId, pending.colId, pending.index, pending.data);
    else removeRow(pending.rowId);
  };

  const deleteColumn = (rowId: number, colId: number, index: number, data: any) => {
    // The row's only column, and it was never assigned a visualization — there's
    // nothing left to clear, so remove the row itself rather than no-op.
    const targetRow = layoutConfig.find((row: any) => row.rowId === rowId);
    if (data === 0 && targetRow?.cols.length === 1) {
      removeRow(rowId);
      return;
    }
    const config = layoutConfig.map((row: any) => {
      if (row.rowId !== rowId) return row;
      if (row.cols.length > 1 && data === 0) return { ...row, cols: row.cols.filter((col: any) => col.id !== colId) };
      if (data > 0) {
        onVisualizationDeleted?.();
        return {
          ...row,
          cols: row.cols.map((col: any) => {
            if (col.id !== colId) return col;
            const { visualizationCode: _visualizationCode, ...rest } = col;
            return { ...rest, data: {} };
          }),
        };
      }
      return row;
    });
    setLayoutConfig(config);
    updateLayoutConfig(config);
  };

  const returnCharts = (rawType: string, _data: any) => {
    const type = normalizeChartType(rawType);
    const selected = visualizationList.find((v) => v.isChecked);
    if (type === CHART_TYPE.METRIC)
      return <ChartRenderer chartType={type} chartData={{ title: selected?.title, count: Math.floor(Math.random() * 100) }} />;
    if (type === CHART_TYPE.TABVIEW)
      return <ChartRenderer chartType={type} chartData={MOCK_TABVIEW_DATA} />;
    if (!RENDERABLE_CHART_TYPES.includes(type)) {
      return <ChartTypePreview chartType={type} />;
    }
    return <ChartRenderer chartType={type} chartData={MOCK_CHART_DATA} />;
  };

  const renderModalChips = () => (
    <div className={styles.modalChips}>
      {['all', ...chartTypes].map(ct => (
        <div
          key={ct}
          onClick={() => setActiveTypeFilter(ct)}
          className={`${styles.modalChip} ${activeTypeFilter === ct ? styles.modalChipActive : ''}`}
        >
          {ct === 'all' ? `All (${getBaseVizList().length})` : ct.charAt(0).toUpperCase() + ct.slice(1)}
        </div>
      ))}
    </div>
  );

  const renderModalVizList = () => (
    <div className={styles.modalVizList}>
      {displayedVizs.length === 0 && (
        getBaseVizList().length === 0 ? (
          <div className={`${styles.modalVizEmpty} ${styles.modalVizEmptyWarning}`}>
            {t('LAYOUT.NO_LINKED_VISUALIZATIONS', { dashboardId: currentDashboard.dashboardId || '' })}
          </div>
        ) : (
          <div className={styles.modalVizEmpty}>{t('EMPTY_STATE.NO_RESULTS')}</div>
        )
      )}
      {displayedVizs.map((viz: any) => {
        const iSel = selectedVizCode === viz.visualizationCode;
        const iconCfg = getVizTypeConfig(viz.chartType);
        return (
          <div
            key={viz.id}
            onClick={() => handleVizSelect(viz)}
            className={`${styles.modalVizItem} ${iSel ? styles.modalVizItemSelected : ''}`}
          >
            <div className={styles.modalVizIcon} style={{ background: iconCfg.bg }}>
              <span className="material-icons" style={{ fontSize: 20, color: iconCfg.color }}>{iconCfg.icon}</span>
            </div>
            <div className={styles.modalVizInfo}>
              <div className={styles.modalVizName}>{viz.title}</div>
              <div className={styles.modalVizType}>
                {(viz.chartType || 'chart').charAt(0).toUpperCase() + (viz.chartType || 'chart').slice(1)}
              </div>
            </div>
            <div className={`${styles.modalVizCheck} ${iSel ? styles.modalVizCheckSelected : ''}`}>
              {iSel && <span className="material-icons" style={{ fontSize: 11, color: 'var(--text-on-primary)' }}>check</span>}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderModalPreview = () => (
    <div className={styles.modalRight}>
      <div className={styles.modalPreviewLabel}>{t('LAYOUT.PREVIEW')}</div>
      <div className={styles.modalPreviewBody}>
        {!selectedViz ? (
          <div className={styles.modalPreviewEmpty}>
            <span className="material-icons" style={{ fontSize: 40, display: 'block', marginBottom: 10, opacity: 0.3 }}>bar_chart</span>
            {t('LAYOUT.PREVIEW_EMPTY')}
          </div>
        ) : (
          <div className={styles.modalPreviewCard}>
            <div className={styles.modalPreviewCardTitle}>{selectedViz.title}</div>
            <div className={styles.modalPreviewCardType}>
              {(selectedViz.chartType || 'chart').charAt(0).toUpperCase() + (selectedViz.chartType || 'chart').slice(1)} chart
            </div>
            <ChartTypePreview chartType={selectedViz.chartType} />
            <div className={styles.modalPreviewCardHint}>{t('LAYOUT.PREVIEW_HINT')}</div>
          </div>
        )}
      </div>
    </div>
  );

  const renderModalFooter = () => (
    <div className={styles.modalFooter}>
      <div className={styles.modalFooterInfo}>
        {selectedViz ? `${t('LAYOUT.SELECTED')}: ${selectedViz.title}` : t('LAYOUT.NO_VIZ_SELECTED')}
      </div>
      <div className={styles.modalFooterActions}>
        <button type="button" onClick={onCancel} className={styles.modalCancelBtn}>{t('ACTION.CANCEL')}</button>
        <button
          disabled={!selectedViz}
          onClick={(e) => saveVisualMapping(e, modalRowId, modalColIndex)}
          className={`${styles.modalAddBtn} ${!selectedViz ? styles.modalAddBtnDisabled : ''}`}
        >
          {t('LAYOUT.ADD_TO_LAYOUT')}
        </button>
      </div>
    </div>
  );

  const renderVizModal = () => {
    if (!showModal) return null;
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContainer}>
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{t('LAYOUT.ADD_VISUALIZATION')}</span>
            <button type="button" onClick={onCancel} className={styles.modalCloseBtn}>
              <span className="material-icons">close</span>
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.modalLeft}>
              <div className={styles.modalSearchWrap}>
                <div className={styles.modalSearchInner}>
                  <span className={`material-icons ${styles.modalSearchIcon}`}>search</span>
                  <input
                    className={styles.modalSearchInput}
                    placeholder={t('LAYOUT.SEARCH_PLACEHOLDER')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {renderModalChips()}
              {renderModalVizList()}
            </div>

            {renderModalPreview()}
          </div>

          {renderModalFooter()}
        </div>
      </div>
    );
  };

  const renderAddCell = (rowId: number, colIndex: number) => (
    <div className="d-flex justify-content-center align-items-center h-100 mx-4">
      <label role="button" onClick={(e) => launchModal(e, rowId, colIndex)}>
        {t('LAYOUT.ADD_VISUALIZATION_CTA')}
      </label>
    </div>
  );

  const getRowColumnLayout = (colCount: number) => {
    if (colCount === 1) return { colClass: 'col-11', chartClass: styles.chart_area, colIndices: [0] };
    if (colCount === 2) return { colClass: 'col-5 me-4', chartClass: styles.chart_area_two, colIndices: [0, 1] };
    return { colClass: 'col-3 me-3', chartClass: styles.chart_area_three, colIndices: [0, 1, 2] };
  };

  const renderColumn = (row: any, ci: number, j: number, colClass: string, chartClass: string) => (
    <div key={ci} className={`${styles.add_visualization} ${colClass}`}>
      <span className="material-icons float-end px-1">drag_indicator</span>
      <span
        className={`${styles.hover_btn} material-icons float-start ps-1`}
        role="button"
        onClick={(e) => { e.stopPropagation(); askBeforeRemovingColumn(row.rowId, row.cols[ci].id, j, Object.keys(row.cols[ci].data).length); }}
      >
        delete
        {visualizationList.map((item) => item.visualizationCode === row.cols[ci].visualizationCode && (
          <span key={item.id} className={`${styles.hover_btn} material-icons float-end ps-2 mt-1`}>
            <Caption text={item.title} />
          </span>
        ))}
      </span>
      {Object.keys(row.cols[ci].data).length === 0
        ? renderAddCell(row.rowId, ci)
        : <div className={`${chartClass} d-flex justify-content-center align-items-center`}>{renderElements && returnCharts(
            visualizationList.find((v) => v.visualizationCode === row.cols[ci].visualizationCode)?.chartType || row.cols[ci].data.chartType,
            row.cols[ci].data.chartData
          )}</div>
      }
    </div>
  );

  const renderAddColumnButton = (colCount: number, rowId: number) => (
    <div
      className={[
        styles.add_btn_two,
        'w-100',
        colCount === 1 ? 'ms-3' : 'ms-2',
        colCount >= 3 ? styles.addColDisabled : '',
      ].join(' ')}
      onClick={(e) => { e.stopPropagation(); if (colCount < 3) addColumn(rowId); }}
      role="button"
      title={colCount >= 3 ? t('LAYOUT.MAX_COLS_TOOLTIP') : t('LAYOUT.ADD_COLUMN')}
    >
      <div className={`d-flex justify-content-center align-items-center h-100 ${colCount >= 3 ? styles.addColDisabledInner : ''}`}>
        {colCount >= 3 ? (
          <>
            <span className={`material-icons ${styles.addColDisabledIcon}`}>block</span>
            <label className={styles.addColDisabledText}>{t('LAYOUT.MAX_COLS_LABEL')}</label>
          </>
        ) : (
          <label>+</label>
        )}
      </div>
    </div>
  );

  const renderLayoutRow = (row: any, j: number) => {
    const colCount = row.cols.length;
    const { colClass, chartClass, colIndices } = getRowColumnLayout(colCount);

    return (
      <div
        key={row.rowId}
        draggable
        onDragStart={(e) => handleRowDragStart(e, j)}
        onDragOver={(e) => handleRowDragOver(e, j)}
        onDrop={(e) => handleRowDrop(e, j)}
        onDragEnd={handleRowDragEnd}
        className={[
          styles.layoutRow,
          dragRowIndex === j ? styles.layoutRowDragging : '',
          dropRowIndex === j && dragRowIndex !== j ? styles.layoutRowDragOver : '',
        ].join(' ')}
      >
        <div className={styles.removeRowWrap}>
          <span className={`${styles.remove_btn} material-icons`} role="button" onClick={() => askBeforeRemovingRow(row.rowId)} title={t('LAYOUT.DELETE_ROW')}>
            remove_circle
          </span>
        </div>
        <div className={`${styles.rowGrab} row m-0 p-0 mt-3 mb-3`}>
          <div className="d-flex flex-row p-0 m-0" id={`rowConfigurations${j}`}>
            {colIndices.map((ci) => renderColumn(row, ci, j, colClass, chartClass))}
            {renderAddColumnButton(colCount, row.rowId)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={tabStyles.helpBanner}>
        <span className="material-icons" style={{ fontSize: 16, color: 'var(--primary-1)', flexShrink: 0 }}>info</span>
        <div dangerouslySetInnerHTML={{ __html: t('LAYOUT.HELP_TEXT') }} />
      </div>

      {reorderBanner && (
        <div className={styles.reorderBanner}>
          <span className="material-icons" style={{ fontSize: 15, flexShrink: 0 }}>swap_vert</span>
          <span>{t('LAYOUT.ROW_REORDERED')}</span>
          <button type="button" className={styles.reorderBannerClose} onClick={() => setReorderBanner(false)}>
            <span className="material-icons" style={{ fontSize: 15 }}>close</span>
          </button>
        </div>
      )}

      {renderVizModal()}
      <ConfirmDialog
        show={pendingRemoval !== null}
        title={t(pendingRemoval?.kind === 'row' ? 'CONFIRM_REMOVE.ROW_TITLE' : 'CONFIRM_REMOVE.TILE_TITLE')}
        message={t(pendingRemoval?.kind === 'row' ? 'CONFIRM_REMOVE.ROW_MESSAGE' : 'CONFIRM_REMOVE.TILE_MESSAGE')}
        confirmLabel={t('CONFIRM_REMOVE.REMOVE')}
        onConfirm={confirmRemoval}
        onCancel={() => setPendingRemoval(null)}
      />

      <div className="container-fluid mt-3 mb-3">
        <div id="layoutConfigurations">
          {layoutConfig && layoutConfig.map((row: any, j: number) => renderLayoutRow(row, j))}
        </div>
        <div className={`${styles.add_btn_one} col-12 mt-3 mb-3`} onClick={addRow} role="button">
          <label onClick={addRow}>+</label>
        </div>
      </div>
      <Input type="hidden" {...register('layoutConfig')} />
    </>
  );
};